import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { finestraDi, periodoPrecedente, sommaGiorni } from '@leopart/calc';
import { allenamentiEndpoints } from '../../../endpoints/allenamenti';
import { ITrainingBlock } from '../../../models/allenamenti';
import { toIsoDate } from '../allenamenti.constants';
import { AnalysisPeriod } from './analysis.constants';

/**
 * Vista Analisi: stato di periodo/riferimento, fetch dell'aggregato
 * (`/training-blocks/analysis`) e — una volta nota la finestra risolta dal
 * server — dei blocchi grezzi dello stesso intervallo (per la tabella, che
 * l'aggregato non restituisce per design: niente dettaglio per-blocco lì).
 *
 * La finestra locale (`finestraDi`) usa la STESSA funzione pura di
 * @leopart/calc che il server usa per calcolare `period.from/to/label`: si
 * usa solo per far navigare le frecce prev/next senza aspettare il
 * round-trip, mai per ricalcolare percentuali o totali (quelli restano
 * un'esclusiva del server, come da design M2/M3 §3.2).
 */
export const useAllenamentiAnalysis = (teamId: string) => {
  const [period, setPeriod] = useState<AnalysisPeriod>('week');
  const [ref, setRef] = useState<string>(() => toIsoDate(new Date()));

  const finestraLocale = useMemo(() => finestraDi(period, ref), [period, ref]);

  const analysisQuery = useQuery({
    queryKey: ['allenamenti-analysis', teamId, period, ref],
    queryFn: async () => (await allenamentiEndpoints.analysis(teamId, period, ref)).data,
    enabled: !!teamId,
  });

  const data = analysisQuery.data;

  const blocksQuery = useQuery({
    queryKey: ['allenamenti-analysis-blocks', teamId, data?.period.from, data?.period.to],
    queryFn: async () =>
      (await allenamentiEndpoints.list(teamId, data!.period.from, data!.period.to)).data,
    enabled: !!teamId && !!data,
  });

  /** Cambio granularità: si riparte da "oggi", altrimenti si resta bloccati su un riferimento fuori contesto. */
  const changePeriod = (next: AnalysisPeriod) => {
    setPeriod(next);
    setRef(toIsoDate(new Date()));
  };

  const goPrev = () => setRef(periodoPrecedente(finestraLocale).da);
  const goNext = () => setRef(finestraDi(period, sommaGiorni(finestraLocale.a, 1)).da);

  return {
    period,
    changePeriod,
    label: data?.period.label ?? finestraLocale.etichetta,
    goPrev,
    goNext,
    data,
    isLoading: analysisQuery.isLoading,
    isError: analysisQuery.isError,
    error: analysisQuery.error,
    refetch: analysisQuery.refetch,
    blocks: (blocksQuery.data ?? []) as ITrainingBlock[],
    isBlocksLoading: blocksQuery.isLoading,
  };
};
